import { load } from 'js-yaml'
import type { InventoryItemExport } from '@/features/inventory/inventory-item-export'
import type { LegendContentResource } from '@/features/legends/api/resources/legend'
import type { LegendDocument } from '@/features/legends/editor/legend-document'
import type { NamedItem } from '@/features/legends/editor/legend-part-dependencies'
import type { Rule } from '@/features/legends/filters/rules'
import {
  mapLegendContentResourceToDocument,
  mapLegendDocumentToContentResource,
} from '@/features/legends/legend-document-mappers'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import { isNotNil } from '@/utils/object-util'

/**
 * A legend version exported to a file: its content as the Legend API stores
 * it, with the service items it adds, so that it can be imported into another
 * workspace. HQ exports the same format.
 */
export interface LegendExport extends LegendContentResource {
  id?: string
  version?: number
  concurrency_version?: number
  workspace_id?: string
  includedInventoryItems?: { item?: InventoryItemExport }[]
}

/**
 * The export of a version. The routing's requirements reference skills by
 * name, so that the workspace the file is imported into can resolve them to
 * its own. Its plans keep the IDs, as HQ's exports do.
 *
 * @param document
 * @param version
 * @param skills The workspace's skills.
 * @param inventoryItems The service items the legend adds.
 */
export function createLegendExport(
  document: LegendDocument,
  version: Pick<LegendVersion, 'version' | 'concurrencyVersion'>,
  skills: NamedItem[],
  inventoryItems: InventoryItemExport[],
): LegendExport {
  const skillNames = new Map(skills.map((s) => [s.id, s.name]))
  return {
    ...mapLegendDocumentToContentResource(withSkillNames(document, skillNames)),
    version: version.version,
    concurrency_version: version.concurrencyVersion,
    includedInventoryItems: inventoryItems.map((item) => ({ item })),
  }
}

/**
 * Reads an exported legend. Throws when the file isn't one.
 *
 * @param text
 */
export function parseLegendExport(text: string) {
  // SAFETY: every field of an export is optional, and the mappers handle each
  // one missing. A file that isn't an export fails the check for stories.
  const parsed = load(text) as LegendExport
  const {
    id: _id,
    version: _version,
    concurrency_version: _concurrencyVersion,
    workspace_id: _workspaceId,
    includedInventoryItems,
    ...content
  } = parsed
  if (!Array.isArray(content.stories)) {
    throw new Error('The file has no stories.')
  }
  return {
    document: mapLegendContentResourceToDocument(content),
    inventoryItems: (includedInventoryItems ?? [])
      .map((i) => i.item)
      .filter(isNotNil),
  }
}

/**
 * @param document
 * @param names Skill names by ID.
 */
function withSkillNames(
  document: LegendDocument,
  names: Map<string, string>,
): LegendDocument {
  const rename = (rule: Rule): Rule => {
    switch (rule.type) {
      case 'HasSkills':
        return {
          ...rule,
          skills: rule.skills?.map((skill) =>
            typeof skill === 'string' ? (names.get(skill) ?? skill) : skill,
          ),
        }
      case 'If':
        return {
          ...rule,
          conditionRule: rule.conditionRule && rename(rule.conditionRule),
          thenRule: rule.thenRule && rename(rule.thenRule),
          elseRule: rule.elseRule && rename(rule.elseRule),
        }
      case 'Conjunction':
        return { ...rule, rules: rule.rules?.map(rename) }
      case 'BinaryOperation':
      case 'Constant':
      case 'Expression':
      case 'Unknown':
        return rule
    }
  }
  const { routing } = document
  return routing
    ? {
        ...document,
        routing: {
          ...routing,
          requirements: routing.requirements?.map(rename),
        },
      }
    : document
}
